import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert, Col, Container, Row, Spinner } from "reactstrap";
import { httpsCallable } from "firebase/functions";
import neoLogo from "../../assets/images/Neo_Logo_C.svg";
import bgImage from "../../assets/images/pay.jpg";
import { functions } from "../../services/firebase";

const STATUS_UI = {
    approved: {
        title: "Pagamento aprovado",
        subtitle: "Sua conta foi liberada e o acesso ao NeoApp ja pode ser feito com o e-mail cadastrado.",
        accent: "#22c55e",
        icon: "mdi-check-decagram",
    },
    pending: {
        title: "Pagamento pendente",
        subtitle: "Recebemos sua solicitacao. O acesso sera liberado assim que o Mercado Pago confirmar a cobranca.",
        accent: "#f59e0b",
        icon: "mdi-timer-sand",
    },
    rejected: {
        title: "Pagamento nao aprovado",
        subtitle: "Voce pode gerar um novo link de pagamento sem recriar a conta.",
        accent: "#ef4444",
        icon: "mdi-close-octagon",
    },
    trial_started: {
        title: "Teste liberado",
        subtitle: "Sua empresa foi criada com o trial ativo. Agora basta entrar no sistema.",
        accent: "#00f3ff",
        icon: "mdi-rocket-launch",
    },
    created: {
        title: "Aguardando processamento",
        subtitle: "Estamos atualizando o status da cobranca. Se necessario, voce pode gerar um novo link abaixo.",
        accent: "#00f3ff",
        icon: "mdi-progress-clock",
    },
};

const CheckoutStatusPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const externalReference = searchParams.get("external_reference") || "";
    const paymentId = searchParams.get("payment_id") || "";
    const resultParam = searchParams.get("result") || "";

    useEffect(() => {
        document.title = "Status do Pagamento | NeoApp";
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const loadStatus = async () => {
            if (!externalReference) {
                setErrorMessage("Nao foi possivel localizar a referencia do checkout. Gere um novo pagamento pela landing page.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setErrorMessage("");

            try {
                const getPublicCheckoutStatus = httpsCallable(functions, "getPublicCheckoutStatus");
                const response = await getPublicCheckoutStatus({
                    externalReference,
                    paymentId: paymentId || null,
                });

                setStatusData(response.data || null);
            } catch (error) {
                console.error("Erro ao consultar status do checkout:", error);
                setErrorMessage(error.message || "Nao foi possivel carregar o status do pagamento.");
            } finally {
                setLoading(false);
            }
        };

        loadStatus();
    }, [externalReference, paymentId]);

    const normalizedStatus = statusData?.status || resultParam || "created";
    const currentUi = STATUS_UI[normalizedStatus] || STATUS_UI.created;

    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        if (!loading && normalizedStatus === "trial_started") {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        navigate("/login");
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [loading, normalizedStatus, navigate]);

    const handleRegenerateCheckout = async () => {
        if (!statusData?.canRegenerate || !statusData?.tenantId || !statusData?.planCode) {
            return;
        }

        setRegenerating(true);
        setErrorMessage("");

        try {
            const createPublicCheckoutPreference = httpsCallable(functions, "createPublicCheckoutPreference");
            const response = await createPublicCheckoutPreference({
                idTenant: statusData.tenantId,
                planCode: statusData.planCode,
                paymentMethod: statusData.selectedPaymentMethod || "PIX",
                originUrl: window.location.origin,
            });

            const data = response.data || {};

            if (data.redirectUrl) {
                window.location.assign(data.redirectUrl);
                return;
            }

            if (data.statusUrl) {
                window.location.assign(data.statusUrl);
            }
        } catch (error) {
            console.error("Erro ao gerar novo link de pagamento:", error);
            setErrorMessage(error.message || "Nao foi possivel gerar um novo link de pagamento.");
            setRegenerating(false);
        }
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                backgroundColor: "#0f1118",
                backgroundImage: `linear-gradient(135deg, rgba(7, 9, 15, 0.9) 0%, rgba(14, 17, 27, 0.82) 55%, rgba(8, 21, 33, 0.78) 100%), url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                padding: "32px 0",
            }}
        >
            <Container fluid className="px-4 px-xl-5">
                <Row className="justify-content-center">
                    <Col xl={6} lg={8}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Link to="/">
                                <img
                                    src={neoLogo}
                                    alt="NeoApp"
                                    height="34"
                                    style={{ filter: "brightness(0) invert(1)" }}
                                />
                            </Link>

                            <Link
                                to="/"
                                className="text-decoration-none"
                                style={{
                                    color: "#dbe4ff",
                                    textTransform: "uppercase",
                                    letterSpacing: "1px",
                                    fontSize: "0.8rem",
                                }}
                            >
                                Voltar para a landing
                            </Link>
                        </div>

                        <div
                            style={{
                                background: "rgba(16, 19, 29, 0.86)",
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                borderRadius: "28px",
                                backdropFilter: "blur(18px)",
                                boxShadow: "0 30px 90px rgba(0, 0, 0, 0.35)",
                                padding: "clamp(24px, 4vw, 40px)",
                            }}
                        >
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner color="light" />
                                    <p className="mb-0 mt-3" style={{ color: "#cbd5e1" }}>
                                        Validando a situacao do seu checkout...
                                    </p>
                                </div>
                            ) : (
                                <React.Fragment>
                                    {errorMessage ? (
                                        <Alert color="danger" className="mb-4">
                                            {String(errorMessage)}
                                        </Alert>
                                    ) : null}

                                    <div
                                        className="d-inline-flex align-items-center justify-content-center mb-4"
                                        style={{
                                            width: "72px",
                                            height: "72px",
                                            borderRadius: "20px",
                                            backgroundColor: `${String(currentUi.accent || "#000")}20`,
                                            color: String(currentUi.accent || "#000"),
                                        }}
                                    >
                                        <i className={`mdi ${String(currentUi.icon || "mdi-help")}`} style={{ fontSize: "2rem" }} />
                                    </div>

                                    <div className="mb-4">
                                        <span
                                            style={{
                                                color: String(currentUi.accent || "#000"),
                                                textTransform: "uppercase",
                                                letterSpacing: "1.5px",
                                                fontSize: "0.78rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            Checkout publico
                                        </span>
                                        <h1
                                            className="mt-2 mb-3"
                                            style={{
                                                color: "#ffffff",
                                                fontWeight: 700,
                                                letterSpacing: "-0.8px",
                                                fontSize: "clamp(2rem, 4vw, 3rem)",
                                            }}
                                        >
                                            {String(currentUi.title || "")}
                                        </h1>
                                        <p
                                            className="mb-0"
                                            style={{
                                                color: "#b9c2d8",
                                                fontSize: "1rem",
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {String(currentUi.subtitle || "")}
                                            {normalizedStatus === "trial_started" && !loading && (
                                                <span className="d-block mt-3" style={{ color: "#00f3ff", fontWeight: "600", fontSize: "0.95rem" }}>
                                                    <Spinner size="sm" className="me-2" style={{ width: "0.8rem", height: "0.8rem" }} />
                                                    Redirecionando para o login em {Number(countdown)} segundo{countdown !== 1 ? 's' : ''}...
                                                </span>
                                            )}
                                        </p>
                                    </div>

                                    <div
                                        className="mb-4"
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                            gap: "14px",
                                        }}
                                    >
                                        <div className="checkout-status-card">
                                            <span>Plano</span>
                                            <strong>{String(statusData?.planTitle || "NeoApp")}</strong>
                                        </div>
                                        <div className="checkout-status-card">
                                            <span>Status</span>
                                            <strong>{String(statusData?.statusLabel || normalizedStatus)}</strong>
                                        </div>
                                        <div className="checkout-status-card">
                                            <span>Metodo</span>
                                            <strong>{String(statusData?.selectedPaymentMethodLabel || "Nao informado")}</strong>
                                        </div>
                                        <div className="checkout-status-card">
                                            <span>Referencia</span>
                                            <strong>{String(statusData?.externalReference || externalReference || "-")}</strong>
                                        </div>
                                    </div>

                                    {statusData?.statusDetailMessage ? (
                                        <div
                                            className="mb-4"
                                            style={{
                                                backgroundColor: "rgba(255, 255, 255, 0.03)",
                                                border: "1px solid rgba(255, 255, 255, 0.06)",
                                                borderRadius: "18px",
                                                padding: "16px 18px",
                                                color: "#d6def1",
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {String(statusData.statusDetailMessage)}
                                        </div>
                                    ) : null}

                                    <div className="d-flex flex-wrap gap-3">
                                        {(normalizedStatus === "approved" || normalizedStatus === "trial_started") ? (
                                            <Link to="/login" className="checkout-status-btn checkout-status-btn-primary">
                                                Entrar no sistema
                                            </Link>
                                        ) : null}

                                        {statusData?.canRegenerate ? (
                                            <button
                                                type="button"
                                                className="checkout-status-btn checkout-status-btn-primary"
                                                onClick={handleRegenerateCheckout}
                                                disabled={regenerating}
                                            >
                                                {regenerating ? <Spinner size="sm" /> : "Gerar novo link"}
                                            </button>
                                        ) : null}

                                        <Link to="/" className="checkout-status-btn checkout-status-btn-secondary">
                                            Voltar para os planos
                                        </Link>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>

            <style>
                {`
                    .checkout-status-card {
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 18px;
                        padding: 16px 18px;
                        min-height: 94px;
                    }

                    .checkout-status-card span {
                        display: block;
                        color: #8d9ab9;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.75rem;
                        margin-bottom: 8px;
                    }

                    .checkout-status-card strong {
                        color: #ffffff;
                        font-size: 1rem;
                        line-height: 1.45;
                    }

                    .checkout-status-btn {
                        min-width: 200px;
                        min-height: 52px;
                        border-radius: 14px;
                        border: 1px solid transparent;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 700;
                        letter-spacing: 0.4px;
                        text-decoration: none;
                        transition: all 0.2s ease;
                        padding: 0 22px;
                    }

                    .checkout-status-btn-primary {
                        background: linear-gradient(135deg, #00f3ff 0%, #5cf7d4 100%);
                        color: #0b1220;
                    }

                    .checkout-status-btn-secondary {
                        background: transparent;
                        border-color: rgba(255, 255, 255, 0.16);
                        color: #ffffff;
                    }

                    .checkout-status-btn:hover {
                        transform: translateY(-1px);
                    }
                `}
            </style>
        </div>
    );
};

export default CheckoutStatusPage;
